/**
 * EnumPickerLookupFooter — the lookup-flavoured footer block of
 * `EnumValuePicker`.
 *
 * Two things live here, both above the shared "Refresh values from data"
 * action:
 *
 *  1. **Provenance** — "Values from `dbo.Statuses` · refreshed 2 h ago".
 *     Rendered only for a column declared with `source: 'lookup'`, so staleness
 *     is visible wherever the values are (spec §Cache & invalidation).
 *  2. **"Search rows in `dbo.Statuses`…"** — the escape hatch into the full
 *     `FkLookupPicker` for columns that resolve to an FK target. The cached
 *     list is capped at 200 keys; the row picker is the way out of that cap
 *     (and the way to search a target table by any column). Worded verbatim
 *     like `EnumCellEditorFooter`'s row — it is the same escape, and two names
 *     for it read as two features.
 *
 * The row picker dialog itself is NOT mounted here — it has to outlive the
 * popover that hosts this footer, so `EnumValuePicker` owns that state and
 * this component only reports the click.
 */

import { RefreshCw, Search } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import type { EnumLookupTarget } from '../../../../../types/electron';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { qualifyLookupTarget } from '../../utils/lookupEnumTarget';

export type EnumPickerLookupFooterProps = {
  /** The declared lookup target — provenance line. Absent for other sources. */
  target: EnumLookupTarget | undefined;
  /** `sampledAt` of the cached pairs, for the "refreshed …" stamp. */
  sampledAt: string | undefined;
  /** Render the row-picker escape (column resolves to an FK target). */
  canBrowse: boolean;
  /**
   * The table the escape opens — named in its label so it reads exactly like
   * the cell editor's. May differ from `target` (the live FK resolution wins
   * over a declared target there) and may be absent on a stale declaration.
   */
  browseTarget?: EnumLookupTarget;
  onBrowseAll: () => void;
};

export function EnumPickerLookupFooter({
  target,
  sampledAt,
  canBrowse,
  browseTarget,
  onBrowseAll,
}: EnumPickerLookupFooterProps) {
  if (!target && !canBrowse) return null;
  return (
    <>
      {target && (
        <div
          className="flex items-start gap-1.5 px-2 pt-1 pb-0.5 text-[11px] leading-tight text-muted-foreground"
          data-testid="enum-value-picker-provenance"
        >
          <RefreshCw className="mt-0.5 h-2.5 w-2.5 shrink-0 opacity-60" aria-hidden="true" />
          <span>
            Values from{' '}
            <span className="font-mono" translate="no">
              {qualifyLookupTarget(target.toSchema, target.toTable)}
            </span>
            {sampledAt ? ` · refreshed ${formatRelativeTime(sampledAt)}` : ''}
          </span>
        </div>
      )}
      {canBrowse && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-xs h-7"
          data-testid="enum-value-picker-browse-rows"
          onClick={onBrowseAll}
        >
          <Search className="h-3 w-3 mr-2" aria-hidden="true" />
          <span className="truncate">
            {browseTarget ? (
              <>
                Search rows in{' '}
                <span translate="no" className="font-medium">
                  {qualifyLookupTarget(browseTarget.toSchema, browseTarget.toTable)}
                </span>
                …
              </>
            ) : (
              'Search rows…'
            )}
          </span>
        </Button>
      )}
    </>
  );
}
